import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/* ── Trust badges data ── */
const TRUST = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="28" height="28">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Authentic Guarantee',
    desc: 'Every piece is handpicked and quality-certified before it reaches you.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="28" height="28">
        <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    title: 'Secure Payments',
    desc: 'All transactions are encrypted and 100% safe.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="28" height="28">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    ),
    title: 'Easy Returns',
    desc: '7-day hassle-free return policy on all orders.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="28" height="28">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    title: 'Visit Our Store',
    desc: 'Experience jewellery in person at Capitol Mall, Kannur.',
  },
];

/* ── Contact info ── */
const INFO = [
  {
    label: 'Email Us',
    value: 'aamiraindia@gmail.com',
    href: 'mailto:aamiraindia@gmail.com',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
  },
  {
    label: 'Phone',
    value: 'To Be Announced',
    href: null,
    soon: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.79a16 16 0 0 0 6.29 6.29l.86-.86a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
  },
  {
    label: 'Store Location',
    value: 'Aamira, 1st Floor, Capitol Mall, Kannur, Kerala 670002',
    href: 'https://maps.google.com/?q=Capitol+Mall+Kannur+Kerala',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
      </svg>
    ),
  },
  {
    label: 'Store Hours',
    value: 'Mon – Sun: 10:00 AM – 10:00 PM',
    href: null,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
];

/* ── Form ── */
const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleSubmit = () => {
    if (!form.name || !form.email || !form.message) return;
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }, 3500);
  };

  return (
    <div className="ct-form-wrap">
      <p className="ct-form-eyebrow">Send a Message</p>
      <h2 className="ct-form-title">We'd love to <em>hear from you</em></h2>
      <p className="ct-form-sub">Questions about an order, a custom piece, or just want to say hello — we're always here.</p>

      {sent ? (
        <motion.div
          className="ct-success"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="ct-success-icon">✦</div>
          <p className="ct-success-title">Message Received</p>
          <p className="ct-success-desc">Thank you for reaching out. We'll get back to you within 24 hours.</p>
        </motion.div>
      ) : (
        <div className="ct-form">
          <div className="ct-row">
            <div className="ct-field">
              <label className="ct-label">Your Name</label>
              <input
                className="ct-input"
                type="text"
                name="name"
                placeholder="Priya Sharma"
                value={form.name}
                onChange={handleChange}
              />
            </div>
            <div className="ct-field">
              <label className="ct-label">Email Address</label>
              <input
                className="ct-input"
                type="email"
                name="email"
                placeholder="priya@example.com"
                value={form.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="ct-field">
            <label className="ct-label">Subject</label>
            <input
              className="ct-input"
              type="text"
              name="subject"
              placeholder="Order enquiry / Custom jewellery / General"
              value={form.subject}
              onChange={handleChange}
            />
          </div>

          <div className="ct-field">
            <label className="ct-label">Your Message</label>
            <textarea
              className="ct-textarea"
              name="message"
              rows="5"
              placeholder="Tell us how we can help you..."
              value={form.message}
              onChange={handleChange}
            />
          </div>

          <button className="ct-submit" onClick={handleSubmit}>
            Send Message &nbsp; →
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Map embed ── */
const MapEmbed = () => (
  <div className="ct-map-wrap">
    <iframe
      title="Aamira Store Location"
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.382!2d75.37812!3d11.874259!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba49dfcbfe63113%3A0xbf784e0f2e7eb9f8!2sCapitol%20Mall!5e0!3m2!1sen!2sin!4v1700000000000"
      width="100%"
      height="100%"
      style={{ border: 0, display: 'block' }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
    <div className="ct-map-label">
      <span className="ct-map-pin">✦</span>
      <span>Aamira · Capitol Mall, Kannur</span>
    </div>
  </div>
);

/* ── Page ── */
const Contact = () => {
  const heroRef = useRef(null);
  const heroView = useInView(heroRef, { once: true, amount: 0.3 });
  const trustRef = useRef(null);
  const trustView = useInView(trustRef, { once: true, amount: 0.2 });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        /* ── Page ── */
        .ct-page {
          background: #FAF8F4;
          min-height: 100vh;
          padding-top: 162px;
          font-family: 'Jost', sans-serif;
          position: relative;
          overflow-x: hidden;
        }
        .ct-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        /* ── Hero ── */
        .ct-hero {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 56px clamp(20px, 5vw, 80px) 50px;
          border-bottom: 1px solid rgba(176,141,87,0.15);
          background: linear-gradient(to bottom, #F0EDE6, #FAF8F4);
        }
        .ct-hero-eyebrow {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.35em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          margin-bottom: 20px;
        }
        .ct-hero-eyebrow::before,
        .ct-hero-eyebrow::after {
          content: '';
          width: 40px; height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .ct-hero-eyebrow::after { background: linear-gradient(to left, transparent, #B08D57); }
        .ct-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(38px, 6vw, 72px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.0;
          letter-spacing: -0.02em;
          margin-bottom: 8px;
        }
        .ct-hero-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
          display: block;
        }
        .ct-hero-desc {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(15px, 1.8vw, 19px);
          font-style: italic;
          font-weight: 300;
          color: #8A7E72;
          letter-spacing: 0.03em;
          margin-top: 12px;
        }
        .ct-hero-tagline {
          font-size: 12px;
          font-weight: 400;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #B08D57;
          margin-top: 24px;
        }

        /* ── Trust strip ── */
        .ct-trust {
          position: relative;
          z-index: 1;
          background: #1C1A18;
          padding: 48px clamp(20px, 5vw, 80px);
        }
        .ct-trust-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          max-width: 1280px;
          margin: 0 auto;
        }
        .ct-trust-item {
          padding: 20px 32px;
          border-right: 1px solid rgba(176,141,87,0.15);
          text-align: center;
        }
        .ct-trust-item:last-child { border-right: none; }
        .ct-trust-icon {
          color: #B08D57;
          display: flex;
          justify-content: center;
          margin-bottom: 14px;
        }
        .ct-trust-title {
          font-family: 'Playfair Display', serif;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          margin-bottom: 6px;
          letter-spacing: 0.01em;
        }
        .ct-trust-desc {
          font-size: 11px;
          font-weight: 300;
          color: rgba(255,255,255,0.5);
          line-height: 1.6;
          letter-spacing: 0.02em;
        }

        /* ── Main section ── */
        .ct-main {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
          padding: 80px clamp(16px, 4vw, 64px) 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: start;
        }

        /* ── Info panel ── */
        .ct-info-panel {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .ct-info-eyebrow {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ct-info-eyebrow::before {
          content: '';
          width: 28px; height: 1px;
          background: #B08D57;
        }
        .ct-info-heading {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 3.5vw, 44px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.1;
          letter-spacing: -0.02em;
          margin-bottom: 8px;
        }
        .ct-info-heading em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .ct-info-sub {
          font-family: 'Cormorant Garamond', serif;
          font-size: 16px;
          font-style: italic;
          font-weight: 300;
          color: #8A7E72;
          line-height: 1.6;
          margin-bottom: 40px;
          letter-spacing: 0.02em;
        }

        /* info items */
        .ct-info-items {
          display: flex;
          flex-direction: column;
          gap: 0;
          border-top: 1px solid rgba(176,141,87,0.15);
        }
        .ct-info-item {
          display: flex;
          align-items: flex-start;
          gap: 18px;
          padding: 22px 0;
          border-bottom: 1px solid rgba(176,141,87,0.15);
          text-decoration: none;
          transition: background 0.2s;
        }
        .ct-info-item-icon {
          color: #B08D57;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .ct-info-item-body {}
        .ct-info-item-label {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 4px;
        }
        .ct-info-item-value {
          font-family: 'Playfair Display', serif;
          font-size: 15px;
          font-weight: 600;
          color: #1C1A18;
          line-height: 1.4;
        }
        .ct-info-item-value.soon {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 400;
          color: #9A9186;
          font-style: italic;
          letter-spacing: 0.06em;
        }
        a.ct-info-item .ct-info-item-value { transition: color 0.2s; }
        a.ct-info-item:hover .ct-info-item-value { color: #B08D57; }

        /* Map */
        .ct-map-wrap {
          position: relative;
          width: 100%;
          height: 220px;
          margin-top: 32px;
          overflow: hidden;
          border: 1px solid rgba(176,141,87,0.2);
        }
        .ct-map-label {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          background: rgba(28,26,24,0.88);
          color: #D4AF78;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 10px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ct-map-pin { color: #B08D57; font-size: 12px; }

        /* ── Form panel ── */
        .ct-form-wrap {
          background: #fff;
          padding: 44px 40px;
          border: 1px solid rgba(176,141,87,0.15);
          box-shadow: 0 24px 80px rgba(28,26,24,0.07);
        }
        .ct-form-eyebrow {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 10px;
        }
        .ct-form-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(22px, 2.5vw, 30px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.15;
          margin-bottom: 10px;
        }
        .ct-form-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .ct-form-sub {
          font-size: 12px;
          font-weight: 300;
          color: #9A9186;
          line-height: 1.7;
          margin-bottom: 32px;
          letter-spacing: 0.02em;
        }
        .ct-form { display: flex; flex-direction: column; gap: 18px; }
        .ct-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .ct-field { display: flex; flex-direction: column; gap: 7px; }
        .ct-label {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #6b6560;
        }
        .ct-input, .ct-textarea {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 400;
          color: #1C1A18;
          background: #FAF8F4;
          border: 1px solid #e8e2d9;
          padding: 13px 16px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          resize: none;
          letter-spacing: 0.01em;
        }
        .ct-input::placeholder, .ct-textarea::placeholder { color: #c4bdb4; }
        .ct-input:focus, .ct-textarea:focus {
          border-color: #B08D57;
          box-shadow: 0 0 0 3px rgba(176,141,87,0.08);
        }
        .ct-submit {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          background: #1C1A18;
          color: #fff;
          border: none;
          padding: 16px 32px;
          cursor: pointer;
          transition: background 0.22s;
          align-self: flex-start;
        }
        .ct-submit:hover { background: #B08D57; }

        /* success */
        .ct-success {
          text-align: center;
          padding: 48px 20px;
          border: 1px solid rgba(176,141,87,0.25);
          background: #FAF8F4;
        }
        .ct-success-icon {
          font-size: 28px;
          color: #B08D57;
          margin-bottom: 16px;
        }
        .ct-success-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          color: #1C1A18;
          margin-bottom: 10px;
        }
        .ct-success-desc {
          font-size: 13px;
          color: #8A7E72;
          line-height: 1.7;
        }

        /* ── Bottom quote strip ── */
        .ct-quote {
          position: relative;
          z-index: 1;
          background: linear-gradient(135deg, #1C1A18 0%, #2E2A24 100%);
          padding: 70px clamp(20px, 6vw, 120px);
          text-align: center;
          margin-top: 80px;
        }
        .ct-quote-ornament {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 32px;
          opacity: 0.4;
        }
        .ct-quote-ornament::before,
        .ct-quote-ornament::after {
          content: '';
          width: 60px; height: 1px;
          background: #B08D57;
        }
        .ct-quote-diamond {
          width: 6px; height: 6px;
          background: #B08D57;
          transform: rotate(45deg);
        }
        .ct-quote-text {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(22px, 3.5vw, 38px);
          font-style: italic;
          font-weight: 300;
          color: #fff;
          line-height: 1.5;
          letter-spacing: 0.02em;
          max-width: 700px;
          margin: 0 auto 16px;
        }
        .ct-quote-attr {
          font-size: 10px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          font-weight: 500;
        }

        /* ornament */
        .ct-ornament {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 60px 0 80px;
          opacity: 0.35;
          position: relative;
          z-index: 1;
        }
        .ct-ornament::before {
          content: '';
          width: 80px; height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .ct-ornament::after {
          content: '';
          width: 80px; height: 1px;
          background: linear-gradient(to left, transparent, #B08D57);
        }
        .ct-diamond-sm {
          width: 6px; height: 6px;
          background: #B08D57;
          transform: rotate(45deg);
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .ct-trust-grid { grid-template-columns: repeat(2, 1fr); }
          .ct-trust-item:nth-child(2) { border-right: none; }
          .ct-trust-item:nth-child(3) { border-top: 1px solid rgba(176,141,87,0.15); }
          .ct-trust-item:nth-child(4) { border-top: 1px solid rgba(176,141,87,0.15); border-right: none; }
          .ct-main { grid-template-columns: 1fr; gap: 40px; }
          .ct-form-wrap { padding: 32px 28px; }
        }
        @media (max-width: 768px) {
          .ct-page { padding-top: 105px; }
          .ct-trust-grid { grid-template-columns: 1fr 1fr; }
          .ct-trust-item { padding: 20px 16px; }
          .ct-row { grid-template-columns: 1fr; }
          .ct-form-wrap { padding: 28px 20px; }
          .ct-trust { padding: 36px 16px; }
        }
        @media (max-width: 480px) {
          .ct-trust-grid { grid-template-columns: 1fr; }
          .ct-trust-item { border-right: none !important; border-top: 1px solid rgba(176,141,87,0.15); }
          .ct-trust-item:first-child { border-top: none; }
        }
      `}</style>

      <div className="ct-page">

        {/* ── Hero ── */}
        <div className="ct-hero" ref={heroRef}>
          <motion.p
            className="ct-hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={heroView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            Aamira Jewellery
          </motion.p>
          <motion.h1
            className="ct-hero-title"
            initial={{ opacity: 0, y: 24 }}
            animate={heroView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.12 }}
          >
            Get in
            <em>Touch</em>
          </motion.h1>
          <motion.p
            className="ct-hero-desc"
            initial={{ opacity: 0 }}
            animate={heroView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.24 }}
          >
            We're here to help you find the perfect piece.
          </motion.p>
          <motion.p
            className="ct-hero-tagline"
            initial={{ opacity: 0 }}
            animate={heroView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.36 }}
          >
            ✦ &nbsp; Reach us anytime — we respond within 24 hours.
          </motion.p>
        </div>

        {/* ── Trust Strip ── */}
        <div className="ct-trust" ref={trustRef}>
          <div className="ct-trust-grid">
            {TRUST.map((t, i) => (
              <motion.div
                key={t.title}
                className="ct-trust-item"
                initial={{ opacity: 0, y: 20 }}
                animate={trustView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="ct-trust-icon">{t.icon}</div>
                <p className="ct-trust-title">{t.title}</p>
                <p className="ct-trust-desc">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Main grid ── */}
        <div className="ct-main">

          {/* Info panel */}
          <motion.div
            className="ct-info-panel"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65 }}
          >
            <p className="ct-info-eyebrow">Contact Details</p>
            <h2 className="ct-info-heading">Always a message <em>away</em></h2>
            <p className="ct-info-sub">Whether you're looking for bridal jewellery, a gift, or just browsing — our team is ready to assist.</p>

            <div className="ct-info-items">
              {INFO.map((item) =>
                item.href ? (
                  <a key={item.label} href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="ct-info-item">
                    <span className="ct-info-item-icon">{item.icon}</span>
                    <div className="ct-info-item-body">
                      <p className="ct-info-item-label">{item.label}</p>
                      <p className="ct-info-item-value">{item.value}</p>
                    </div>
                  </a>
                ) : (
                  <div key={item.label} className="ct-info-item">
                    <span className="ct-info-item-icon">{item.icon}</span>
                    <div className="ct-info-item-body">
                      <p className="ct-info-item-label">{item.label}</p>
                      <p className={`ct-info-item-value${item.soon ? ' soon' : ''}`}>{item.value}</p>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Map */}
            <MapEmbed />
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, delay: 0.1 }}
          >
            <ContactForm />
          </motion.div>

        </div>

        {/* ── Quote strip ── */}
        <motion.div
          className="ct-quote"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <div className="ct-quote-ornament">
            <div className="ct-quote-diamond" />
          </div>
          <p className="ct-quote-text">
            "Jewellery is not just an accessory — it is the language of love, memory, and legacy."
          </p>
          <p className="ct-quote-attr">— Aamira Jewellery, Kannur</p>
        </motion.div>

        {/* Ornament */}
        <motion.div
          className="ct-ornament"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.35 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="ct-diamond-sm" />
        </motion.div>

      </div>
    </>
  );
};

export default Contact;